import {
  FaCcAmex,
  FaCcDinersClub,
  FaCcDiscover,
  FaCcJcb,
  FaCcMastercard,
  FaCcVisa,
  FaCreditCard,
} from 'react-icons/fa';

/**
 * Maps card brand names to their corresponding Font Awesome icon components
 * @param {string} brand - The card brand name (e.g., 'visa', 'mastercard', 'amex')
 * @returns {React.Component} The corresponding Font Awesome icon component
 */
export const getCardIcon = (brand) => {
  const brandLower = brand?.toLowerCase() || '';

  const iconMap = {
    visa: FaCcVisa,
    mastercard: FaCcMastercard,
    amex: FaCcAmex,
    'american express': FaCcAmex,
    discover: FaCcDiscover,
    diners: FaCcDinersClub,
    'diners club': FaCcDinersClub,
    jcb: FaCcJcb,
    unionpay: FaCreditCard,
    unknown: FaCreditCard,
  };

  return iconMap[brandLower] || FaCreditCard;
};

/**
 * Formats a card expiration date from month and year to MM/YY format
 * @param {number} expMonth - The expiration month (1-12)
 * @param {number} expYear - The expiration year (e.g., 2025)
 * @returns {string} Formatted expiration date (e.g., "12/25")
 */
export const formatCardExpiration = (expMonth, expYear) => {
  if (!expMonth || !expYear) return '';

  return `${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}`;
};

/**
 * Formats a card number to show only the last 4 digits with masking
 * @param {string} last4 - The last 4 digits of the card
 * @returns {object} Object with masked and last4 parts
 */
export const formatCardNumber = (last4) => {
  return {
    masked: '****',
    last4: last4 || '****',
  };
};

/**
 * Gets a user-friendly display name for a card brand
 * @param {string} brand - The card brand name from Stripe
 * @returns {string} User-friendly brand name
 */
export const getCardBrandDisplayName = (brand) => {
  const brandLower = brand?.toLowerCase() || '';

  const displayNames = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    'american express': 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    'diners club': 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
    unknown: 'Card',
  };

  return displayNames[brandLower] || 'Card';
};

/**
 * Cardholder name validation rules based on payment industry standards
 * - Characters: Letters (A-Z, a-z), spaces, hyphens, apostrophes, periods
 * - Length: 2-26 characters (industry standard max)
 * - Must start with a letter
 * - Must end with a letter or period (for initials)
 * - No consecutive spaces
 */
export const CARDHOLDER_NAME_RULES = [
  { required: true, message: 'Please enter the cardholder name' },
  { min: 2, message: 'Name must be at least 2 characters' },
  { max: 26, message: 'Name cannot exceed 26 characters' },
  {
    pattern: /^[A-Za-z][A-Za-z\s\-'.]*[A-Za-z.]$/,
    message: 'Name can only contain letters, spaces, hyphens, apostrophes, and periods',
  },
  {
    validator: (_, value) => {
      if (value && /\s{2,}/.test(value)) {
        return Promise.reject(new Error('Name cannot contain consecutive spaces'));
      }

      return Promise.resolve();
    },
  },
];
